import React from 'react';
import {
  Boxes,
  Bug,
  Container,
  FileKey2,
  KeyRound,
  Lock,
  Mail,
  ShieldCheck,
  Trash2,
} from 'lucide-react';

const pillars = [
  {
    icon: Lock,
    title: 'Encryption in transit',
    body: 'Public endpoints are TLS-only (1.2+, 1.3 preferred) behind the Cloudflare edge. Internal hops across machines run over encrypted tunnels; plaintext HTTP never leaves a host.',
  },
  {
    icon: FileKey2,
    title: 'Encryption at rest',
    body: 'Databases, git repos, build artifacts, and uploads live on encrypted volumes. Backups are encrypted before they leave the origin machine.',
  },
  {
    icon: KeyRound,
    title: 'Key handling',
    body: 'Platform API keys are stored as salted hashes. Plaintext is shown once at creation and can only be rotated, never recovered. Provider keys and app secrets are injected at runtime, scrubbed from build and CI logs.',
  },
  {
    icon: Container,
    title: 'Isolation model',
    body: 'Agent runs and app workloads run in dedicated containers; GPU/CPU jobs run in per-job VMs on the upstream provider. Runs get scoped, short-lived credentials; untrusted code is sandboxed with network, filesystem, and time limits.',
  },
  {
    icon: Trash2,
    title: 'Data deletion',
    body: 'Export or delete your account from settings; both are audit-logged. Deletion removes account rows, repos, deployments, and stored artifacts. Backups age out on their normal rotation (30 days or less).',
  },
  {
    icon: ShieldCheck,
    title: 'Access control',
    body: 'Organization RBAC (owner / admin / member / viewer), per-org audit logs, and every admin action gated server-side.',
  },
];

const subprocessors = [
  { name: 'Hetzner', purpose: 'Compute for hosted apps, agents, and databases', location: 'Germany / Finland / US' },
  { name: 'Cloudflare', purpose: 'Edge network, DNS, TLS termination, tunnels', location: 'Global' },
  { name: 'Google Cloud / Firebase', purpose: 'Sign-in (Google, GitHub, email) and identity tokens', location: 'US / EU' },
  { name: 'Stripe', purpose: 'Payments and billing; card data never touches our servers', location: 'US' },
  { name: 'AWS (SES)', purpose: 'Transactional email (invites, receipts, resets)', location: 'US' },
  { name: 'GitHub', purpose: 'Optional repo integration and the GitHub App webhook', location: 'US' },
  { name: 'RunPod', purpose: 'Serverless GPU execution tier for some workloads', location: 'US / EU' },
  {
    name: 'Model providers (OpenAI, Anthropic, Google, and others)',
    purpose: 'Inference, only for requests you route to them through the gateway',
    location: 'US / EU',
  },
];

export const SecurityPage: React.FC = () => (
  <main className="overflow-hidden bg-black text-white">
    <section className="relative border-b border-white/10 px-4 pb-14 pt-24 sm:px-8 lg:px-12">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(56,189,248,0.12),transparent_55%)]" />
      <div className="relative mx-auto max-w-[1400px]">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/58">
          <ShieldCheck size={12} className="text-sky-400" />
          Security and trust
        </div>
        <h1 className="mt-6 max-w-[20ch] font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
          How app.nz protects your code, data, and keys
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-8 text-white/72">
          Encryption, key handling, isolation, subprocessors, and how to report a problem. For feature
          controls, see{' '}
          <a href="/enterprise" className="font-semibold text-sky-400 hover:text-sky-300">enterprise</a>.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="mailto:security@app.nz" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-sky-400 px-6 text-sm font-bold text-black transition hover:bg-sky-300">
            <Bug size={16} /> Report a vulnerability
          </a>
          <a href="/enterprise" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-6 text-sm font-bold text-white transition hover:bg-white hover:text-black">
            Enterprise controls
          </a>
        </div>
      </div>
    </section>

    <section className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
      <div className="mx-auto max-w-[1400px]">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-400/90">the model</p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Six things we hold to</h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {pillars.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                <Icon size={20} className="text-sky-400" />
                <h3 className="mt-4 font-display text-lg font-bold">{p.title}</h3>
                <p className="mt-2 text-sm leading-6 text-white/65">{p.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    <section className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
      <div className="mx-auto max-w-[1400px]">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-400/90">vulnerability reporting</p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">How to report a vulnerability</h2>
        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-white/45">
              <Mail size={14} className="text-sky-400" /> Contact
            </p>
            <p className="mt-4 text-sm leading-7 text-white/72">
              Email <a href="mailto:security@app.nz" className="font-semibold text-sky-400 hover:text-sky-300">security@app.nz</a>{' '}
              with reproduction steps and impact. A person replies, usually within one business day, with
              updates until it is resolved.
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-white/45">
              <ShieldCheck size={14} className="text-sky-400" /> Safe harbor
            </p>
            <p className="mt-4 text-sm leading-7 text-white/72">
              Good-faith research on your own accounts is welcome: no legal action for testing that avoids
              service disruption, other customers' data, and social engineering. Give us reasonable time to
              fix before disclosure; we credit reporters.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
      <div className="mx-auto max-w-[1400px]">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-400/90">subprocessors</p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Our subprocessors</h2>
        <p className="mt-5 max-w-3xl text-base leading-8 text-white/72">
          Model providers see only requests you route to them; the gateway never mirrors traffic to other providers.
        </p>
        <div className="mt-8 overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 text-[11px] font-bold uppercase tracking-[0.16em] text-white/45">
                <th className="px-5 py-4">Subprocessor</th>
                <th className="px-5 py-4">Purpose</th>
                <th className="px-5 py-4">Location</th>
              </tr>
            </thead>
            <tbody>
              {subprocessors.map((s) => (
                <tr key={s.name} className="border-b border-white/5 last:border-0">
                  <td className="px-5 py-4 font-semibold text-white/85">{s.name}</td>
                  <td className="px-5 py-4 leading-6 text-white/65">{s.purpose}</td>
                  <td className="px-5 py-4 text-white/55">{s.location}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <section className="px-4 py-14 sm:px-8 sm:py-20 lg:px-12">
      <div className="relative mx-auto max-w-[1400px] overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center sm:px-12">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(56,189,248,0.14),transparent_60%)]" />
        <div className="relative">
          <Boxes size={22} className="mx-auto text-sky-400" />
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-4xl">Security review and contact</h2>
          <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-8 text-white/70">
            Questionnaires, DPAs, and architecture walkthroughs go through{' '}
            <a href="mailto:enterprise@app.nz" className="font-semibold text-sky-400 hover:text-sky-300">enterprise@app.nz</a>.
            Vulnerabilities go to <a href="mailto:security@app.nz" className="font-semibold text-sky-400 hover:text-sky-300">security@app.nz</a>.
          </p>
        </div>
      </div>
    </section>
  </main>
);

export default SecurityPage;
